import { useCallback, useEffect, useRef, useState } from "react";
import { getProgress, setProgress, VocabularyProgress } from "../utils/vocabularyProgressStorage";
import { getCloudProgress, putCloudProgress } from "../utils/progressCloud";
import { useAuth } from "../context/AuthContext";
import { ProgressSyncStatus } from "./progressTypes";

const CLOUD_WRITE_DEBOUNCE_MS = 800;
const FEATURE_KEY = "vocabularyProgress";

export function useVocabularyProgress() {
    const { status, user } = useAuth();
    const [progress, setProgressState] = useState<VocabularyProgress>(() => getProgress());
    const [syncStatus, setSyncStatus] = useState<ProgressSyncStatus>("local");
    const writeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const latestProgressRef = useRef(progress);
    latestProgressRef.current = progress;

    useEffect(() => {
        // Progress tracking is an account feature. A signed-out visitor gets an
        // empty set rather than whatever is left in localStorage, so the UI
        // never shows a learned state that the visitor has no way to change.
        if (status === "unauthenticated") {
            setProgressState({});
            setSyncStatus("signedOut");
            return undefined;
        }

        if (status !== "authenticated" || !user?.identityId) {
            setProgressState(getProgress());
            setSyncStatus("local");
            return undefined;
        }

        let cancelled = false;
        setSyncStatus("loading");

        getCloudProgress(user.identityId, FEATURE_KEY)
            .then((remote) => {
                if (cancelled) return;
                if (remote?.data && Object.keys(remote.data).length > 0) {
                    setProgressState(remote.data);
                }
                setSyncStatus("ready");
            })
            .catch(() => {
                if (!cancelled) setSyncStatus("error");
            });

        return () => {
            cancelled = true;
        };
    }, [status, user?.identityId]);

    useEffect(() => {
        return () => {
            if (writeTimeoutRef.current !== null) clearTimeout(writeTimeoutRef.current);
        };
    }, []);

    const setLearned = useCallback(
        (wordId: string, learned: boolean) => {
            if (status === "unauthenticated") return;
            setProgressState((current) => {
                const next = { ...current };
                if (learned) {
                    next[wordId] = "learned";
                } else {
                    delete next[wordId];
                }
                setProgress(next);

                if (status === "authenticated" && user?.identityId) {
                    if (writeTimeoutRef.current !== null) clearTimeout(writeTimeoutRef.current);
                    writeTimeoutRef.current = setTimeout(() => {
                        putCloudProgress(user.identityId, FEATURE_KEY, latestProgressRef.current).catch(() => {
                            setSyncStatus("error");
                        });
                    }, CLOUD_WRITE_DEBOUNCE_MS);
                }

                return next;
            });
        },
        [status, user?.identityId]
    );

    return { progress, setLearned, syncStatus };
}
